/**
 * Speicher-Zustand einer Seite für den Toast-Mechanismus von `PageTemplate`.
 *
 * `PageTemplate` macht aus dem Übergang `isSaving` true→false den „Gespeichert"-Toast und aus
 * einem neuen `saveError` den Fehler-Toast (siehe docs/adr/004_autosave_dirty_check.md). Der
 * Zustand ist bewusst getrennt von `error` eines Data-Hooks: das trägt den blockierenden
 * Ladefehler und ersetzt den Seiteninhalt, ein Speicherfehler tut das nicht.
 *
 * `speichern` kapselt die immer gleiche Abfolge und **wirft nicht** – der Fehler steht danach
 * in `saveError`. Rückgabe ist das Ergebnis des Schreibvorgangs, im Fehlerfall `null`; wer den
 * angelegten oder aktualisierten Datensatz weiterverwendet, prüft darauf.
 *
 * Der Dirty-Check gehört **davor**, nicht hier hinein: sobald `speichern` läuft, ist der Toast
 * nicht mehr zu unterdrücken. Er braucht den bisherigen Datensatz und gehört deshalb in den
 * Data-Hook der Domäne (`hasFieldChanged`/`hasAnyFieldChanged`/`hasListChanged` aus
 * `utils/formUtils`).
 */
import { useCallback, useState } from "react";
import { parseApiError } from "../services/api/errorPresentation";

export interface SaveState {
  isSaving: boolean;
  saveError: string | null;
  speichern: <T>(schreiben: () => Promise<T>) => Promise<T | null>;
}

export function useSaveState(): SaveState {
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const speichern = useCallback(
    async <T>(schreiben: () => Promise<T>): Promise<T | null> => {
      setIsSaving(true);
      setSaveError(null);
      try {
        return await schreiben();
      } catch (err) {
        setSaveError(parseApiError(err).message);
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [],
  );

  return { isSaving, saveError, speichern };
}
